import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Inbox, MessageSquarePlus, Send } from "lucide-react";
import { errorMessage } from "../../../api/client";
import { clockApi } from "../../../api/clock";
import { adminApi, smsApi } from "../../../api/resources";
import type { EmployeePublic, SmsEvent } from "../../../api/types";
import { PageHeader } from "../../../components/ComingSoon";
import { Modal, SelectFilter } from "../../../components/Overlay";
import { Button, ErrorText, Field, TextInput } from "../../../components/ui";
import { useAuth } from "../../../context/AuthContext";
import { fmtDate } from "../../../utils/labels";
import { useTextGuide } from "../../../api/textGuide";
import { DataTable, LoadState, useLoad } from "../shared";
import { EVENT_STATUS_TONE, PreviewToolBadge } from "./smsUi";

const STATUSES = ["", "RECEIVED", "PROCESSED", "SENT", "FAILED", "REPLIED", "RATE_LIMITED", "BLOCKED_COMPLIANCE", "SUPPRESSED_OPT_OUT", "INVALID_SIGNATURE"];
const INTENTS = ["", "HELP", "STATUS", "STOP", "START", "IN", "OUT", "UNKNOWN", "VERIFICATION"];
const opts = (xs: string[], all: string): [string, string][] => xs.map((x) => [x, x ? x.replace(/_/g, " ").toLowerCase() : all]);

function Simulator({ onSent }: { onSent: () => void }) {
  const [emps, setEmps] = useState<EmployeePublic[] | null>(null);
  const [from, setFrom] = useState("");
  const [body, setBody] = useState("HELP");
  const { guide } = useTextGuide();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    adminApi.employees().then(setEmps).catch(() => setEmps([]));
  }, []);
  const empId = emps?.find((x) => x.phone_e164 === from)?.id;
  const shiftNow = async () => {
    setError(null);
    try {
      const r = await clockApi.shiftNow(empId!);
      const a = r.assignment;
      setResult(`${r.created ? "Created" : "Already has"} a CONFIRMED shift now: ${a.work_date} ${a.start_time_local ?? ""}–${a.end_time_local ?? ""}`);
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const send = async (text: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await smsApi.simulate(from, text);
      const reply = r.twiml.match(/<Message>([\s\S]*?)<\/Message>/)?.[1];
      setResult(r.status_code === 200 ? (reply ? `Reply: ${reply.replace(/&amp;/g, "&")}` : "No reply sent (by design).") : `Webhook returned ${r.status_code}`);
      onSent();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void send(body);
  };
  return (
    <form onSubmit={submit} data-testid="sms-simulator">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <PreviewToolBadge testId="sms-simulator-preview-badge" />
        <span className="text-xs text-zinc-400">Sends a correctly signed request to the real webhook.</span>
      </div>
      <div className="grid gap-4 md:grid-cols-[1fr_1fr_1.4fr_auto] md:items-end">
        <Field label="Pick an employee" htmlFor="sim-employee">
          <select id="sim-employee" onChange={(e) => e.target.value && setFrom(e.target.value)} defaultValue="" data-testid="sms-simulator-employee-select"
            className="h-11 w-full rounded-sm border border-white/15 bg-surface px-3 text-sm text-white">
            <option value="">— any number —</option>
            {(emps ?? []).filter((x) => x.phone_e164).map((x) => <option key={x.id} value={x.phone_e164!}>{x.full_name}</option>)}
          </select>
        </Field>
        <Field label="From number" htmlFor="sim-from">
          <TextInput id="sim-from" value={from} onChange={(e) => setFrom(e.target.value)} placeholder="+14155550123" data-testid="sms-simulator-from-input" />
        </Field>
        <Field label="Message body" htmlFor="sim-body">
          <TextInput id="sim-body" value={body} onChange={(e) => setBody(e.target.value)} data-testid="sms-simulator-body-input" />
        </Field>
        <Button type="submit" loading={busy} disabled={!from} data-testid="sms-simulator-send-button"><Send className="h-4 w-4" /> Send</Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {[...(guide?.commands.map((c) => c.example) ?? []), "hello"].map((k) => (
          <button key={k} type="button" onClick={() => setBody(k)} data-testid={`sms-simulator-quick-${k.toLowerCase().replace(" ", "-")}`} className="rounded-sm border border-white/15 px-2 py-1 font-mono text-[11px] text-zinc-300 hover:border-white/40">{k}</button>
        ))}
      </div>
      <div data-testid="sms-simulator-clock-tools" className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/10 pt-4">
        <span className="label-caps mr-2 text-zinc-400">Timeclock</span>
        <Button type="button" variant="secondary" className="h-9 px-3 text-[11px]" disabled={!empId} onClick={shiftNow} data-testid="sms-simulator-shift-now-button">Create shift now</Button>
        <Button type="button" className="h-9 px-3 text-[11px]" disabled={!from || busy} onClick={() => send("IN")} data-testid="sms-simulator-send-in-button">Text IN</Button>
        <Button type="button" className="h-9 px-3 text-[11px]" disabled={!from || busy} onClick={() => send("OUT")} data-testid="sms-simulator-send-out-button">Text OUT</Button>
        <span className="text-xs text-zinc-500">Pick an employee first. The GPS link + QR appear in the Mock SMS Outbox.</span>
      </div>
      <ErrorText message={error} testId="sms-simulator-error" />
      {result && <p data-testid="sms-simulator-result" className="mt-3 rounded-sm border border-white/10 bg-ink p-3 text-sm text-zinc-200">{result}</p>}
    </form>
  );
}

export default function AdminSmsActivity() {
  const { config } = useAuth();
  const [f, setF] = useState<Record<string, string | undefined>>({});
  const [reload, setReload] = useState(0);
  const [simOpen, setSimOpen] = useState(false);
  const closeSim = useCallback(() => setSimOpen(false), []);
  const [emps, setEmps] = useState<EmployeePublic[]>([]);
  useEffect(() => {
    adminApi.employees().then(setEmps).catch(() => setEmps([]));
  }, []);
  const key = JSON.stringify(f);
  const loader = useCallback(() => smsApi.events(JSON.parse(key)), [key, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useLoad<SmsEvent[]>(loader);
  const set = (k: string, v: string) => setF((x) => ({ ...x, [k]: v || undefined }));

  return (
    <section data-testid="admin-sms-activity-page" className="animate-rise">
      <PageHeader title="SMS Activity">
        {config?.is_preview && (
          <Button variant="secondary" onClick={() => setSimOpen(true)} data-testid="sms-simulator-open-button"><MessageSquarePlus className="h-4 w-4" /> Mock SMS Inbound</Button>
        )}
        {config?.is_preview && (
          <Link to="/admin/sms-activity/outbox" data-testid="mock-outbox-link" className="inline-flex h-11 items-center gap-2 rounded-sm border border-sky-400/50 px-4 text-xs font-semibold uppercase tracking-[0.12em] text-sky-300 hover:bg-sky-400/10">
            <Inbox className="h-4 w-4" /> Mock SMS Outbox
          </Link>
        )}
      </PageHeader>
      {config?.is_preview && (
        <Modal open={simOpen} onClose={closeSim} title="Mock SMS Inbound" testId="sms-simulator-modal" wide trapFocus>
          <Simulator onSent={() => setReload((r) => r + 1)} />
        </Modal>
      )}
      <div data-testid="sms-filters" className="mb-6 flex flex-wrap items-end gap-3">
        <SelectFilter label="Direction" testId="sms-filter-direction" value={f.direction ?? ""} onChange={(v) => set("direction", v)} options={[["", "All directions"], ["INBOUND", "Inbound"], ["OUTBOUND", "Outbound"]]} />
        <SelectFilter label="Employee" testId="sms-filter-employee" value={f.employee_id ?? ""} onChange={(v) => set("employee_id", v)} options={[["", "All employees"], ...emps.map((e): [string, string] => [e.id, e.full_name])]} />
        <SelectFilter label="Keyword" testId="sms-filter-intent" value={f.intent ?? ""} onChange={(v) => set("intent", v)} options={opts(INTENTS, "All keywords")} />
        <SelectFilter label="Status" testId="sms-filter-status" value={f.status ?? ""} onChange={(v) => set("status", v)} options={opts(STATUSES, "All statuses")} />
        <div className="w-44"><TextInput type="date" value={f.date_from ?? ""} onChange={(e) => set("date_from", e.target.value)} className="[color-scheme:dark]" data-testid="sms-filter-date-from" aria-label="From date" /></div>
        <div className="w-44"><TextInput type="date" value={f.date_to ?? ""} onChange={(e) => set("date_to", e.target.value)} className="[color-scheme:dark]" data-testid="sms-filter-date-to" aria-label="To date" /></div>
      </div>
      <LoadState error={error} loading={!data && !error} testId="sms-events-error" />
      {data && (
        <DataTable testId="sms-events-table" headers={["When", "Direction", "From → To", "Employee", "Message", "Keyword", "Status"]} empty={data.length === 0}>
          {data.map((e) => (
            <tr key={e.id} data-testid={`sms-event-row-${e.id}`} className="align-top hover:bg-white/[0.03]">
              <td className="whitespace-nowrap px-5 py-3 text-xs text-zinc-400">{fmtDate(e.occurred_at)}</td>
              <td className="px-5 py-3 text-xs uppercase tracking-wider text-zinc-300">{e.direction === "INBOUND" ? "In" : "Out"}</td>
              <td className="whitespace-nowrap px-5 py-3 font-mono text-xs text-zinc-300">{e.from_masked ?? "—"} → {e.to_masked ?? "—"}</td>
              <td className="px-5 py-3 text-zinc-200">{e.employee_id ? <Link to={`/admin/employees/${e.employee_id}`} className="hover:text-signal">{e.employee_name}</Link> : <span className="text-zinc-600">Unknown</span>}</td>
              <td data-testid={`sms-event-body-${e.id}`} className="max-w-md px-5 py-3 text-xs text-zinc-300">{e.body ?? "—"}{e.template_key && <span className="ml-2 font-mono text-[10px] text-zinc-500">{e.template_key}</span>}</td>
              <td className="px-5 py-3 font-mono text-xs text-zinc-400">{e.intent ?? "—"}</td>
              <td data-testid={`sms-event-status-${e.id}`} className={`px-5 py-3 text-xs font-semibold ${EVENT_STATUS_TONE[e.status] ?? "text-zinc-300"}`}>{e.status.replace(/_/g, " ")}{e.delivery_status && <span className="block font-normal text-zinc-500">{e.delivery_status}</span>}</td>
            </tr>
          ))}
        </DataTable>
      )}
    </section>
  );
}
